/**
 * Stadium venue data — geometry, pricing, seat layouts
 * Matches the Pacific Amphitheatre layout from reference image
 * Total ~8,500 seats
 *
 * Focal point at (700, 680). Arcs radiate upward (stage at bottom).
 * Angles: 0=right, π/2=down, π=left (standard canvas convention)
 */

const D = (deg) => deg * Math.PI / 180;

// ─── COLOR PALETTE ─────────────────────────────────────────────
export const COLORS = {
  bg: '#f1f5f9',
  stage: '#1A1D30',
  stageText: '#D4D4E0',
  stageGlow: '#6366F1',
  pit: '#10B981',
  circle: '#06B6D4',
  orchestra: '#8B5CF6',
  terrace: '#F59E0B',
  boxes: '#F472B6',
  selected: '#22D3EE',
  ada: '#3B82F6',
};

// ─── FOCAL POINT ───────────────────────────────────────────────
const CX = 700, CY = 680;

// ─── SECTION DEFINITIONS ───────────────────────────────────────
// Layout (stage→outward): Pit → Circle → Sections 1,2,3 → Boxes → Sections 4,5,6,7,8 → GA Lawn
// No overlaps. Sections at same ring are side-by-side angularly.
export const SECTIONS = [
  // ── RING 0: PIT ──
  {
    id: 'pit', label: 'Pit', shortLabel: 'PIT',
    dataKey: 'Pit (196 Seats)', color: COLORS.pit,
    priceRange: [166, 230], displayPrice: '$166+', seatCount: 170,
    rows: ['AAA','BBB','CCC','DDD','EEE','FFF','GGG'],
    seatsPerRow: [34,32,30,28,22,16,8],
    seatAngularPad: 0.06,
    geo: { cx: CX, cy: CY, innerRadius: 45, outerRadius: 120, startAngle: D(215), endAngle: D(325) },
  },
  // ── RING 1: CIRCLE ──
  {
    id: 'circle', label: 'Circle', shortLabel: 'CIRCLE',
    dataKey: 'Circle (285 Seats)', color: COLORS.circle,
    priceRange: [58, 92], displayPrice: '$58+', seatCount: 285,
    rows: ['AA','BB','CC','DD','EE'],
    seatsPerRow: [48,53,61,69,54],
    geo: { cx: CX, cy: CY, innerRadius: 155, outerRadius: 260, startAngle: D(205), endAngle: D(335) },
  },
  // ── RING 2: ORCHESTRA (Sections 1, 2, 3) ──
  {
    id: 'section1', label: 'Section 1', shortLabel: 'Section 1',
    dataKey: 'Section 1', adaKey: 'Section 1 ADA', color: COLORS.orchestra,
    priceRange: [46, 56], displayPrice: '$46+', seatCount: 767,
    rows: ['A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y','Z'],
    seatsPerRow: [16,18,20,21,23,25,26,28,29,31,33,34,36,38,39,41,42,44,46,47,49,51,19,11],
    seatStarts: { 'Y': 34, 'Z': 44 },
    reverseNumbering: true,
    lastRowAlign: 'start',
    geo: { cx: CX, cy: CY, innerRadius: 290, outerRadius: 395, startAngle: D(185), endAngle: D(228) },
  },
  {
    id: 'section2', label: 'Section 2', shortLabel: 'Section 2',
    dataKey: 'Section 2', color: COLORS.orchestra,
    priceRange: [46, 56], displayPrice: '$46+', seatCount: 948,
    rows: ['A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V'],
    seatsPerRow: [33,34,37,40,42,43,44,45,46,47,48,50,51,52,53,54,55,57,58,59],
    geo: { cx: CX, cy: CY, innerRadius: 290, outerRadius: 425, startAngle: D(233), endAngle: D(307) },
  },
  {
    id: 'section3', label: 'Section 3', shortLabel: 'Section 3',
    dataKey: 'Section 3', adaKey: 'Section 3 ADA', color: COLORS.orchestra,
    priceRange: [46, 56], displayPrice: '$46+', seatCount: 733,
    rows: ['C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y','Z'],
    seatsPerRow: [20,21,23,25,26,28,29,31,33,34,36,38,39,41,42,44,46,47,49,51,19,11],
    seatStarts: { 'Y': 34, 'Z': 44 },
    lastRowAlign: 'end',
    geo: { cx: CX, cy: CY, innerRadius: 290, outerRadius: 395, startAngle: D(312), endAngle: D(355) },
  },
  // ── RING 2.5: BOXES (center only) ──
  {
    id: 'boxes', label: 'Premium Box Seats', shortLabel: 'PREMIUM BOX',
    dataKey: 'Box Seats', color: COLORS.boxes, isBoxSection: true,
    priceRange: [1012, 1012], displayPrice: '$1,012', seatCount: 65,
    rows: ['A','B','C'], seatsPerRow: [32,30,3],
    geo: { cx: CX, cy: CY, innerRadius: 445, outerRadius: 500, startAngle: D(233), endAngle: D(307) },
  },
  // ── RING 3: TERRACE (Sections 8, 5, 6, 7, 4) ──
  {
    id: 'section8', label: 'Section 8', shortLabel: 'Section 8',
    dataKey: 'Section 8', color: COLORS.terrace,
    priceRange: [28, 42], displayPrice: '$28+', seatCount: 890,
    rows: ['A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y','Z'],
    seatsPerRow: [27,27,28,33,33,34,35,35,36,36,37,38,38,39,39,40,41,41,42,43,43,44,40,41],
    reverseNumbering: true,
    rowNotches: {
      Y: { afterSeat: 8, gapUnits: 2.4 },
      Z: { afterSeat: 8, gapUnits: 2.4 },
    },
    geo: { cx: CX, cy: CY, innerRadius: 445, outerRadius: 660, startAngle: D(183), endAngle: D(206) },
  },
  {
    id: 'section5', label: 'Section 5', shortLabel: 'Section 5',
    dataKey: 'Section 5', adaKey: 'Section 5 ADA', color: COLORS.terrace,
    priceRange: [28, 42], displayPrice: '$28+', seatCount: 963,
    rows: ['A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y'],
    seatsPerRow: [24,25,27,33,34,36,37,39,40,42,43,45,46,48,49,51,52,54,55,57,58,60,8],
    seatStarts: { 'Y': 53 },
    lastRowAlign: 'end',
    geo: { cx: CX, cy: CY, innerRadius: 445, outerRadius: 625, startAngle: D(310), endAngle: D(332) },
  },
  {
    id: 'section6', label: 'Section 6', shortLabel: 'Section 6',
    dataKey: 'Section 6', color: COLORS.terrace,
    priceRange: [28, 42], displayPrice: '$28+', seatCount: 1420,
    rows: ['A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y','Z'],
    seatsPerRow: [56,56,56,60,60,60,60,60,60,60,60,60,60,60,60,60,60,60,60,60,60,60,56,56],
    rowNotches: {
      Y: { afterSeat: 28, gapUnits: 2.4 },
      Z: { afterSeat: 28, gapUnits: 2.4 },
    },
    geo: { cx: CX, cy: CY, innerRadius: 515, outerRadius: 660, startAngle: D(233), endAngle: D(307) },
  },
  {
    id: 'section7', label: 'Section 7', shortLabel: 'Section 7',
    dataKey: 'Section 7', adaKey: 'Section 7 ADA', color: COLORS.terrace,
    priceRange: [28, 42], displayPrice: '$28+', seatCount: 963,
    rows: ['A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y'],
    seatsPerRow: [24,25,27,33,34,36,37,39,40,42,43,45,46,48,49,51,52,54,55,57,58,60,8],
    seatStarts: { 'Y': 53 },
    reverseNumbering: true,
    lastRowAlign: 'start',
    geo: { cx: CX, cy: CY, innerRadius: 445, outerRadius: 625, startAngle: D(208), endAngle: D(230) },
  },
  {
    id: 'section4', label: 'Section 4', shortLabel: 'Section 4',
    dataKey: 'Section 4', color: COLORS.terrace,
    priceRange: [28, 42], displayPrice: '$28+', seatCount: 897,
    rows: ['A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y','Z'],
    seatsPerRow: [27,27,28,33,33,34,35,35,36,37,38,38,39,39,40,41,41,42,43,43,43,44,40,41],
    rowNotches: {
      Y: { afterSeat: 8, gapUnits: 2.4 },
      Z: { afterSeat: 8, gapUnits: 2.4 },
    },
    geo: { cx: CX, cy: CY, innerRadius: 445, outerRadius: 660, startAngle: D(334), endAngle: D(357) },
  },
];

// Compute label positions (center of each arc sector)
SECTIONS.forEach(s => {
  const g = s.geo;
  const midR = (g.innerRadius + g.outerRadius) / 2;
  const midA = (g.startAngle + g.endAngle) / 2;
  s.labelPos = { x: g.cx + Math.cos(midA) * midR, y: g.cy + Math.sin(midA) * midR };
});

// ─── ADA SECTIONS (silver bands at outer edge of sections 1,3,5,7) ──
export const ADA_SECTIONS = [
  {
    id: 'section1-ada', parentId: 'section1', label: 'Section 1 ADA',
    dataKey: 'Section 1 ADA', color: COLORS.ada, isADA: true,
    rows: ['Z'], seatsPerRow: [28],
    priceRange: [46, 46], displayPrice: '$46+', seatCount: 28,
    alignSide: 'end',
    reverseNumbering: true,
    geo: { cx: CX, cy: CY, innerRadius: 384, outerRadius: 394, startAngle: D(204), endAngle: D(228) },
  },
  {
    id: 'section3-ada-back', parentId: 'section3', label: 'Section 3 ADA (Back)',
    dataKey: 'Section 3 ADA', color: COLORS.ada, isADA: true,
    rows: ['Z'], seatsPerRow: [28],
    priceRange: [39, 39], displayPrice: '$39+', seatCount: 28,
    alignSide: 'start',
    geo: { cx: CX, cy: CY, innerRadius: 384, outerRadius: 394, startAngle: D(312), endAngle: D(336) },
  },
  {
    id: 'section3-ada-front', parentId: 'section3', label: 'Section 3 ADA (Front)',
    dataKey: 'Section 3 ADA', color: COLORS.ada, isADA: true,
    rows: ['B'], seatsPerRow: [14],
    priceRange: [58, 58], displayPrice: '$58+', seatCount: 14,
    geo: { cx: CX, cy: CY, innerRadius: 280, outerRadius: 290, startAngle: D(312), endAngle: D(327) },
  },
  {
    id: 'section5-ada', parentId: 'section5', label: 'Section 5 ADA',
    dataKey: 'Section 5 ADA', color: COLORS.ada, isADA: true,
    rows: ['Z'], seatsPerRow: [32],
    priceRange: [28, 28], displayPrice: '$28+', seatCount: 32,
    geo: { cx: CX, cy: CY, innerRadius: 625, outerRadius: 635, startAngle: D(310), endAngle: D(322) },
  },
  {
    id: 'section7-ada', parentId: 'section7', label: 'Section 7 ADA',
    dataKey: 'Section 7 ADA', color: COLORS.ada, isADA: true,
    rows: ['Z'], seatsPerRow: [32],
    priceRange: [28, 28], displayPrice: '$28+', seatCount: 32,
    reverseNumbering: true,
    geo: { cx: CX, cy: CY, innerRadius: 625, outerRadius: 635, startAngle: D(218), endAngle: D(230) },
  },
];

// ─── BOX DEFINITIONS (8 premium boxes within the boxes arc) ──────
const boxesSection = SECTIONS.find(s => s.id === 'boxes');
const _bGeo = boxesSection.geo;
const _bTotal = _bGeo.endAngle - _bGeo.startAngle;
const _bGap = D(1);
const _bWidth = (_bTotal - 7 * _bGap) / 8;

export const BOX_DEFS = [
  { id: 'B1', label: 'Box 1', rows: [{ name: 'A', count: 4 }, { name: 'B', count: 4 }], seatCount: 8 },
  { id: 'B2', label: 'Box 2', rows: [{ name: 'A', count: 4 }, { name: 'B', count: 4 }], seatCount: 8 },
  { id: 'B3', label: 'Box 3', rows: [{ name: 'A', count: 4 }, { name: 'B', count: 4 }], seatCount: 8 },
  { id: 'B4', label: 'Box 4', rows: [{ name: 'C', count: 3, isADA: true }, { name: 'B', count: 2 }, { name: 'A', count: 4 }], seatCount: 9 },
  { id: 'B5', label: 'Box 5', rows: [{ name: 'A', count: 4 }, { name: 'B', count: 4 }], seatCount: 8 },
  { id: 'B6', label: 'Box 6', rows: [{ name: 'A', count: 4 }, { name: 'B', count: 4 }], seatCount: 8 },
  { id: 'B7', label: 'Box 7', rows: [{ name: 'A', count: 4 }, { name: 'B', count: 4 }], seatCount: 8 },
  { id: 'B8', label: 'Box 8', rows: [{ name: 'A', count: 4 }, { name: 'B', count: 4 }], seatCount: 8 },
];

BOX_DEFS.forEach((box, i) => {
  const sa = _bGeo.startAngle + i * (_bWidth + _bGap);
  box.geo = {
    cx: _bGeo.cx, cy: _bGeo.cy,
    innerRadius: _bGeo.innerRadius, outerRadius: _bGeo.outerRadius,
    startAngle: sa, endAngle: sa + _bWidth,
  };
});

// ─── STAGE ─────────────────────────────────────────────────────
export const STAGE = {
  x: 500, y: 720, width: 400, height: 100, radius: 10,
  color: COLORS.stage, label: 'STAGE', labelColor: COLORS.stageText,
};

// ─── CANVAS ────────────────────────────────────────────────────
export const CANVAS_W = 1400;
export const CANVAS_H = 820;

// ─── ZOOM TARGETS (bounding-box based, includes ADA band) ──────
export function getZoomTarget(sectionId, viewW, viewH, mobilePad) {
  const sec = SECTIONS.find(s => s.id === sectionId);
  if (!sec) return { cx: 700, cy: 400, scale: 1 };

  const geos = [sec.geo];
  const adaSecs = ADA_SECTIONS.filter(a => a.parentId === sectionId);
  adaSecs.forEach(a => geos.push(a.geo));

  const N = 48;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const g of geos) {
    for (let i = 0; i <= N; i++) {
      const a = g.startAngle + (g.endAngle - g.startAngle) * i / N;
      const cosA = Math.cos(a), sinA = Math.sin(a);
      const ix = g.cx + cosA * g.innerRadius, iy = g.cy + sinA * g.innerRadius;
      const ox = g.cx + cosA * g.outerRadius, oy = g.cy + sinA * g.outerRadius;
      if (ix < minX) minX = ix; if (ix > maxX) maxX = ix;
      if (iy < minY) minY = iy; if (iy > maxY) maxY = iy;
      if (ox < minX) minX = ox; if (ox > maxX) maxX = ox;
      if (oy < minY) minY = oy; if (oy > maxY) maxY = oy;
    }
  }

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const bw = maxX - minX;
  const bh = maxY - minY;
  const pad = mobilePad || 1.18;
  const vw = viewW || 1400, vh = viewH || 900;
  const scale = Math.min(vw / (bw * pad), vh / (bh * pad));
  return { cx, cy, scale };
}

// ─── SEAT GENERATION — radial fan algorithm ─────────────────────
//
// Mathematical basis:
//   Each row sits on a fixed radius from the focal point (CX, CY).
//   A single SEAT_WIDTH (physical px, center-to-center along arc) is
//   derived from the densest row in the section: the row where
//   arcLength / seatCount is smallest.
//
//   For every row:
//     angularStep  = SEAT_WIDTH / radius          (constant arc gap)
//     rowSpan      = (seatCount − 1) × angularStep
//     rowStart     = midAngle − rowSpan / 2       (centered)
//
//   Result:
//     • Narrow front rows naturally have fewer seats
//     • Wide back rows naturally have more seats
//     • Physical gap between adjacent seats is identical everywhere
//     • Rows are centered → clean fan edges
//     • No restrictedRows, seatStarts, gridAlign, or dual-geometry hacks
//
export function generateRadialSeats(section) {
  const seats = [];

  // ══ Special path: Box section → generate seats per sub-box ══
  if (section.isBoxSection) {
    BOX_DEFS.forEach(box => {
      const g = box.geo;
      const pad = 3;
      const innerR = g.innerRadius + pad;
      const outerR = g.outerRadius - pad;
      const depth = outerR - innerR;
      const aPad = 0.008;
      const bSa = g.startAngle + aPad;
      const bEa = g.endAngle - aPad;
      const bMid = (bSa + bEa) / 2;
      const bSpan = bEa - bSa;
      const totalRows = box.rows.length;
      // Pack rows tightly in center of box (use only 40% of depth)
      const usableDepth = depth * 0.4;
      const midR = (innerR + outerR) / 2;
      const rowSpacing = totalRows > 1 ? usableDepth / (totalRows - 1) : 0;

      box.rows.forEach((rowDef, ri) => {
        const offset = totalRows === 1 ? 0 : (ri - (totalRows - 1) / 2) * rowSpacing;
        const radius = midR + offset;
        const cnt = rowDef.count;
        const angStep = cnt > 1 ? bSpan / (cnt + 1) : 0;
        for (let s = 0; s < cnt; s++) {
          const angle = cnt === 1 ? bMid : bSa + (s + 1) * angStep;
          seats.push({
            id: `boxes-${box.id}-${rowDef.name}-${s + 1}`,
            sectionId: 'boxes',
            boxId: box.id,
            row: rowDef.name,
            seatNumber: s + 1,
            x: g.cx + Math.cos(angle) * radius,
            y: g.cy + Math.sin(angle) * radius,
            radius: Math.min(rowSpacing * 0.32, 2.8),
            price: section.priceRange[0],
            isADA: !!rowDef.isADA,
            status: 'available',
          });
        }
      });
    });
    return seats;
  }

  // ══ Special path: ADA aligned to parent's last row (sections 1 & 3) ══
  if (section.alignSide && section.parentId) {
    const parent = SECTIONS.find(s => s.id === section.parentId);
    if (parent) {
      const pPad    = 6;
      const pInnerR = parent.geo.innerRadius + pPad;
      const pOuterR = parent.geo.outerRadius - pPad;
      const pDepth  = pOuterR - pInnerR;
      const pTotal  = parent.rows.length;
      const pAP     = parent.seatAngularPad || 0.015;
      const pSa     = parent.geo.startAngle + pAP;
      const pEa     = parent.geo.endAngle   - pAP;
      const pSpan   = pEa - pSa;
      const lastR   = pOuterR;

      let pSW = Infinity;
      for (let i = 0; i < pTotal; i++) {
        const t = pTotal === 1 ? 0.5 : i / (pTotal - 1);
        const w = (pInnerR + t * pDepth) * pSpan / parent.seatsPerRow[i];
        if (w < pSW) pSW = w;
      }
      if (!isFinite(pSW)) pSW = 10;
      const pRS  = pTotal > 1 ? pDepth / (pTotal - 1) : pDepth;
      const dotR = Math.min(Math.min(pRS, pSW) * 0.32, 2.2);
      const step = pSW / lastR;

      for (let r = 0; r < section.rows.length; r++) {
        const rn  = section.rows[r];
        const cnt = section.seatsPerRow[r];
        const sp  = (cnt - 1) * step;
        const rs  = section.alignSide === 'start' ? pSa : pEa - sp;
        for (let s = 0; s < cnt; s++) {
          const a = cnt === 1 ? (section.alignSide === 'start' ? pSa : pEa) : rs + s * step;
          const seatNum = section.reverseNumbering
            ? 1 + (cnt - 1 - s)
            : s + 1;
          seats.push({
            id: `${section.id}-${rn}-${seatNum}`, sectionId: section.id,
            row: rn, seatNumber: seatNum,
            x: parent.geo.cx + Math.cos(a) * lastR,
            y: parent.geo.cy + Math.sin(a) * lastR,
            radius: dotR, price: section.priceRange[0],
            isADA: true, status: 'available',
          });
        }
      }
      return seats;
    }
  }

  // ══ Normal path ══
  const geo = section.geo;
  const totalRows = section.rows.length;
  const radPad = section.isADA ? 1 : 6;
  const innerR = geo.innerRadius + radPad;
  const outerR = geo.outerRadius - radPad;
  const depth  = outerR - innerR;
  const angPad    = section.seatAngularPad || 0.015;
  const sa        = geo.startAngle + angPad;
  const ea        = geo.endAngle   - angPad;
  const angleSpan = ea - sa;
  const midAngle  = (sa + ea) / 2;
  const rowSpacing = totalRows > 1 ? depth / (totalRows - 1) : depth;

  // Step 1: SEAT_WIDTH from densest row
  let seatWidth = Infinity;
  for (let r = 0; r < totalRows; r++) {
    const t = totalRows === 1 ? 0.5 : r / (totalRows - 1);
    const rad = innerR + t * depth;
    const w = (rad * angleSpan) / section.seatsPerRow[r];
    if (section.seatsPerRow[r] > 0 && w < seatWidth) seatWidth = w;
  }
  if (!isFinite(seatWidth)) seatWidth = 10;

  // Step 2: dot radius
  const seatRadius = Math.min(Math.min(rowSpacing, seatWidth) * 0.32, 2.2);

  // Step 3: grid vs fan mode
  const maxSeats = Math.max(...section.seatsPerRow);
  const minSeats = Math.min(...section.seatsPerRow.filter(n => n > 0));
  const useGrid  = maxSeats / minSeats < 1.15;

  let gridStep = 0, gridStart = 0;
  if (useGrid) {
    let densestR = innerR, minW = Infinity;
    for (let r = 0; r < totalRows; r++) {
      const t = totalRows === 1 ? 0.5 : r / (totalRows - 1);
      const rad = innerR + t * depth;
      const w = (rad * angleSpan) / section.seatsPerRow[r];
      if (w < minW) { minW = w; densestR = rad; }
    }
    gridStep  = seatWidth / densestR;
    gridStart = midAngle - (maxSeats - 1) * gridStep / 2;
  }

  // Step 4: place seats
  for (let r = 0; r < totalRows; r++) {
    const rowName   = section.rows[r];
    const seatCount = section.seatsPerRow[r];
    const seatStart = section.seatStarts?.[rowName] || 1;
    const rowNotch  = section.rowNotches?.[rowName] || null;
    const t         = totalRows === 1 ? 0.5 : r / (totalRows - 1);
    const radius    = innerR + t * depth;

    // Side-align short rows (rows with seatStarts) and last row
    let side = null;
    if (section.lastRowAlign && (r === totalRows - 1 || (section.seatStarts && section.seatStarts[rowName]))) side = section.lastRowAlign;

    for (let s = 0; s < seatCount; s++) {
      const seatNum = section.reverseNumbering
        ? seatStart + (seatCount - 1 - s)
        : seatStart + s;
      let angle;
      if (useGrid) {
        const startCol = (maxSeats - seatCount) / 2;
        if (rowNotch && seatCount > 1) {
          const gapUnits = rowNotch.gapUnits || 1;
          const step = ((seatCount - 1) * gridStep) / ((seatCount - 1) + gapUnits);
          const split = section.reverseNumbering ? seatNum <= rowNotch.afterSeat : seatNum > rowNotch.afterSeat;
          const notchOffset = split ? gapUnits : 0;
          angle = gridStart + startCol * gridStep + (s + notchOffset) * step;
        } else {
          angle = gridStart + (startCol + s) * gridStep;
        }
      } else {
        const angStep = seatCount > 1 ? seatWidth / radius : 0;
        const rowSpan = (seatCount - 1) * angStep;
        let rowStart;
        if (side === 'start')    rowStart = sa;
        else if (side === 'end') rowStart = ea - rowSpan;
        else                     rowStart = midAngle - rowSpan / 2;
        if (seatCount === 1) {
          angle = side === 'start' ? sa : side === 'end' ? ea : midAngle;
        } else if (rowNotch) {
          const gapUnits = rowNotch.gapUnits || 1;
          const step = rowSpan / ((seatCount - 1) + gapUnits);
          const split = section.reverseNumbering ? seatNum <= rowNotch.afterSeat : seatNum > rowNotch.afterSeat;
          const notchOffset = split ? gapUnits : 0;
          angle = rowStart + (s + notchOffset) * step;
        } else {
          angle = rowStart + s * angStep;
        }
      }
      seats.push({
        id:         `${section.id}-${rowName}-${seatNum}`,
        sectionId:  section.id,
        row:        rowName,
        seatNumber: seatNum,
        x:          geo.cx + Math.cos(angle) * radius,
        y:          geo.cy + Math.sin(angle) * radius,
        radius:     seatRadius,
        price:      section.priceRange[0],
        isADA:      !!section.isADA,
        status:     'available',
      });
    }
  }
  return seats;
}
